import { useContext, useEffect, useRef } from 'react';
import GameContext from 'contexts/GameContext';
import { useTableSelector } from 'hooks/useTableSelector';

import { useServerTimeout } from 'components/UserActions/ServerTimeoutBanner';

import { voteFromSeatAction } from './voteFromAction';

/**
 * How long a cast vote may go unacknowledged before the player is told. The
 * same ~2 s a betting action waits on its ack before raising the banner, and
 * well inside the shortest vote window, so there is still time to act on it.
 */
export const VOTE_DELIVERY_TIMEOUT_MS = 2000;

/**
 * EN-10761: a multi-run vote the server never acknowledges must not fail in
 * silence.
 *
 * Neither vote writer replies to the emit, so the only proof a vote landed is
 * the server echoing it: this seat's `p{n}action` carrying the choice, or a
 * decline collapsing `runs` and closing the window. GameService already refuses
 * a vote on a socket it KNOWS is down, and raises the "taking longer than
 * expected" banner (rejectUnsendableTableCommand). What it cannot see is a link
 * that has died without saying so yet — a Wi-Fi drop, low bandwidth — which
 * still reads as connected: the tap goes to a dead socket, no frame comes back,
 * and nothing on screen said so. That was QA's NG.
 *
 * So this watches the one thing that is certain: a vote cast locally and not
 * echoed within VOTE_DELIVERY_TIMEOUT_MS raises that same banner, and an echo
 * arriving afterwards takes it down again. It counts wall-clock time rather
 * than frames, because a dead link is exactly the case where frames stop.
 * Giving the buttons back is not its job: Game's reconcileVote already does
 * that when frames keep arriving without the echo, and does nothing when
 * frames stop, which is the right call without evidence either way.
 *
 * Mounted inside the table's ServerTimeoutProvider, beside the action slot
 * rather than in the vote panel, because the panel unmounts the moment the vote
 * is cast (AC-2.2): the wait starts exactly when the panel is gone.
 */
const VoteDeliveryWatch = () => {
  const { playerMultiRunVote } = useContext(GameContext);
  const runs = useTableSelector((table) => table.game.runs);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const seatAction = useTableSelector((table) => table.players?.[seatId]?.action);
  const { showServerTimeout, hideServerTimeout } = useServerTimeout();

  const windowOpen = String(runs ?? '').includes('v');
  const cast = playerMultiRunVote !== undefined && playerMultiRunVote !== '0';
  // The server's record of this seat's vote, read whether or not the window is
  // still open: a decline records the seat action and collapses `runs` in the
  // same frame, so its echo arrives with the window already shut.
  const echoed = cast && voteFromSeatAction(seatAction) !== '0';
  const awaitingEcho = windowOpen && cast && !echoed;

  // Did THIS watch raise the banner, for the vote in this window? Only then may
  // it take it down: the banner is shared with the betting actions' own ack path.
  const raisedRef = useRef(false);

  useEffect(() => {
    if (!awaitingEcho) return undefined;

    const timeoutId = setTimeout(() => {
      raisedRef.current = true;
      showServerTimeout();
    }, VOTE_DELIVERY_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [awaitingEcho, showServerTimeout]);

  // The echo is the proof the vote landed after all: the banner's "check your
  // connection" no longer applies, so it comes down. A window that settles with
  // no echo proves nothing either way: the banner is left to hide itself, and
  // the watch forgets it, so a later vote's echo can never take down a banner
  // something else has raised since.
  useEffect(() => {
    if (!raisedRef.current) return;

    if (echoed) {
      raisedRef.current = false;
      hideServerTimeout();
    } else if (!windowOpen) {
      raisedRef.current = false;
    }
  }, [echoed, windowOpen, hideServerTimeout]);

  return null;
};

export default VoteDeliveryWatch;
